import * as Highcharts from 'highcharts';

import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ChartTemplateService} from '../../../../cockpit/cockpit-shared/charts-templates/chart-template.service';
import {CockpitChartComponent} from '../../../../cockpit/cockpit-shared/cockpit-content-component';
import {ChartsM2} from 'shared/dtos/cockpit/charts/charts';
import {Lohnbaender} from 'shared/dtos/cockpit/charts/lohnbaender';
import {DataStorageService} from 'shared/services/data-storage.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-chart-dialog-lohnband',
  templateUrl: './chart-dialog-lohnband.component.html',
  providers: [ChartTemplateService],
  standalone: false
})
export class ChartDialogLohnbandComponent
  extends CockpitChartComponent
  implements OnInit, OnDestroy
{
  lohnbaender: Lohnbaender;
  private langChangeSubscription: Subscription;

  constructor(
    @Inject(ChartTemplateService)
    private readonly chartTemplateService: ChartTemplateService,
    private readonly dataStorageService: DataStorageService,
    private readonly translateService: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    this.dataStorageService.getChartsData().then((data: ChartsM2) => {
      this.lohnbaender = data.lohnbaender;
      this.initialize(this.lohnbaender);

      this.langChangeSubscription =
        this.translateService.onLangChange.subscribe(() => {
          const svgContainer = document.getElementById('svgChartLohnbaender');
          if (svgContainer) {
            this.initialize(this.lohnbaender);
          }
        });
    });
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  exportChart() {
    this.chart.exportChart(
      {
        type: 'application/pdf',
        filename: 'my-pdf'
      },
      {}
    );
  }

  initialize(data: Lohnbaender) {
    this.chartTemplateService.chartname = 'lohnbaender';
    this.initHighCharts(data);
  }

  private initHighCharts(data: Lohnbaender) {
    const series = this.prepareSeriesData(data);

    this.initializeHighchartsSeriesType();
    const options: Highcharts.Options = this.getHighchartsOptionsFromFiddle();

    const additionalOptions: Highcharts.Options = {
      title: {
        text: this.translateService.instant(
          'm2.cockpit.card.funktionswert.chart.lohnband.title'
        )
      },
      xAxis: [
        {
          title: {
            text: this.translateService.instant(
              'm2.cockpit.card.funktionswert.chart.lohnband.x-Achse'
            )
          },
          accessibility: {
            description: this.translateService.instant(
              'm2.cockpit.card.funktionswert.chart.lohnband.x-Achse'
            )
          },
          tickPositions: data.tickpositions
        }
      ],
      yAxis: {
        title: {
          text: this.translateService.instant(
            'm2.cockpit.card.funktionswert.chart.lohnband.y-Achse'
          )
        }
      },
      plotOptions: {
        series: {borderWidth: 6}
      },
      credits: this.chartTemplateService.getCredit(),
      exporting: this.chartTemplateService.getExportMenus(),
      lang: this.chartTemplateService.getHighchartsTranslate(),
      series
    };
    // Create chart
    const chart = Highcharts.chart(
      'svgChartLohnbaender',
      this.merge(options, additionalOptions),

      thechart => {
        this.callBackFuncToNormalizeBarChart(thechart);

        // Observer pour détecter les changements de taille du conteneur
        const resizeObserver = new ResizeObserver(() => {
          if (this.chart) {
            this.chart.reflow();
          }
        });
        resizeObserver.observe(document.getElementById('svgChartLohnbaender'));
      }
    );

    // Remove click events on container to avoid having "clickable" announced by AT
    // These events are needed for custom click events, drag to zoom, and navigator
    // support.
    chart.container.onmousedown = null;
    chart.container.onclick = null;
  }

  private callBackFuncToNormalizeBarChart(thechart: Highcharts.Chart) {
    this.chart = thechart;
    thechart.reflow();
  }

  private prepareSeriesData(data: Lohnbaender) {
    // Parse and Sanitize, Translate
    const series = JSON.parse(
      this.chartTemplateService.translateChart(
        data.series
          // Fix an error in date Where "NA" was sent instead of null
          .replace(/"NA"/gi, 'null')
      )
    );
    series.forEach(el => {
      el.name = this.translateService.instant(el.name);
    });
    return series;
  }

  private initializeHighchartsSeriesType(): void {
    // Define custom series type for displaying low/med/high values using boxplot as a base
    Highcharts.seriesType(
      'lowmedhigh',
      'boxplot',
      {
        keys: ['low', 'high', 'med', 'mean'],
        tooltip: {
          pointFormat: `<span style="color:{point.color}">\u25CF</span> {series.name}: <br/><span style="color:{series.color}">${this.translateService.instant(
            'm2.cockpit.card.funktionswert.chart.lohnband.min'
          )}</span>: <b>{point.low:.2f}</b><br/><span style="color:{series.color}">${this.translateService.instant(
            'm2.cockpit.card.funktionswert.chart.lohnband.max'
          )}</span>: <b>{point.high:.2f}</b><br/><span style="color:{series.color}">${this.translateService.instant(
            'i18n.common.med'
          )}</span>: <b>{point.med:.2f}</b><br/><span style="color:{series.color}">${this.translateService.instant(
            'i18n.common.mean'
          )}</span>: <b>{point.mean:.2f}</b><br/>`,
          headerFormat: `${this.translateService.instant(
            'm2.cockpit.card.funktionswert.chart.lohnband.x-Achse'
          )}: {point.key}<br/>`
        }
      },
      {
        // Change point shape to a line with three crossing lines for low/median/high
        // Stroke width is hardcoded to 1 for simplicity
        drawPoints() {
          // eslint-disable-next-line @typescript-eslint/no-this-alias, consistent-this
          const series = this;
          this.points.forEach(point => {
            let {graphic} = point;
            const verb = graphic ? 'animate' : 'attr',
              {shapeArgs} = point,
              {width} = shapeArgs,
              left = Math.floor(shapeArgs.x) + 0.5,
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              right = left + width,
              crispX = left + Math.round(width / 2) + 0.5,
              highPlot = Math.floor(point.highPlot) + 0.5,
              // Sneakily draw low marker even if 0
              lowPlot =
                Math.floor(point.lowPlot) + 0.5 - (point.low === 0 ? 1 : 0);

            if (point.isNull) {
              return;
            }

            if (!graphic) {
              // eslint-disable-next-line no-multi-assign
              point.graphic = graphic = series.chart.renderer
                .path('point')
                .add(series.group);
            }

            graphic.attr({
              stroke: point.color || series.color,
              'stroke-width': 1
            });

            graphic[verb]({
              d: [
                'M',
                left,
                highPlot,
                'H',
                right,
                'M',
                left,
                lowPlot,
                'H',
                right,
                'M',
                crispX,
                highPlot,
                'V',
                lowPlot
              ]
            });
          });
        }
      }
    );
  }

  private getHighchartsOptionsFromFiddle(): Highcharts.Options {
    // Copied from Jsfiddle, reomved series for readability, cast as any satisfies lint
    return {
      chart: {
        type: 'lowmedhigh'
      },
      title: {
        text: 'm2.cockpit.card.funktionswert.chart.lohnband.title'
      },
      accessibility: {
        point: {
          descriptionFormatter(point) {
            // Use default formatter for null points
            if (point.isNull) {
              return false;
            }

            return `${point.category}, low ${point.low}, high ${point.high}`;
          }
        },
        series: {
          descriptionFormat:
            '{series.name}, series {seriesNumber} of {chart.series.length} with {series.points.length} data points.'
        }
      },
      xAxis: [
        {
          accessibility: {
            description: 'm2.cockpit.card.funktionswert.chart.lohnband.x-Achse'
          },
          categories: [
            10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26,
            27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
            44, 45, 46, 47, 48, 49, 50
          ],
          tickPositions: [23, 27, 28, 29]
        }
      ],
      yAxis: {
        title: {
          text: 'm2.cockpit.card.funktionswert.chart.lohnband.y-Achse'
        }
      },
      tooltip: {
        shared: true,
        stickOnContact: true
      },
      plotOptions: {
        series: {
          stickyTracking: true,
          whiskerWidth: 5
        }
      }
    } as any;
  }

  private merge(
    options: Highcharts.Options,
    additionalOptions: Highcharts.Options
  ) {
    const merged = {...options, ...additionalOptions};
    merged.xAxis[0] = {...options.xAxis[0], ...additionalOptions.xAxis[0]};
    merged.plotOptions.series = {
      ...options.plotOptions.series,
      ...additionalOptions.plotOptions.series
    };

    return merged;
  }
}
